<script setup lang="ts">
import { ref } from "vue";

import { useFocusTrap } from "@/composables/useFocusTrap";

/**
 * Right-hand slide-over over a scrim. Hosts "Add subject" and
 * "How focus grows" (PRD PE-5.1, PE-7.7) — surfaces that are a detour from the
 * page, never a destination with a route.
 *
 * The caller mounts/unmounts this with `v-if`; the trap and focus restore ride
 * on the component lifecycle.
 */

withDefaults(
  defineProps<{ title: string; subtitle?: string; width?: number }>(),
  { width: 460 },
);

const emit = defineEmits<{ close: [] }>();

const panel = ref<HTMLElement | null>(null);
useFocusTrap(panel);
</script>

<template>
  <div class="scrim rl-anim-overlay-in" @click="emit('close')">
    <aside
      ref="panel"
      class="panel rl-anim-slide-x"
      role="dialog"
      aria-modal="true"
      :aria-label="title"
      :style="{ width: `${width}px` }"
      @click.stop
      @keydown.escape="emit('close')"
    >
      <header class="panel-head">
        <div>
          <h2 class="panel-title">{{ title }}</h2>
          <p v-if="subtitle" class="panel-sub">{{ subtitle }}</p>
        </div>
        <button
          type="button"
          class="panel-close"
          aria-label="Close"
          @click="emit('close')"
        >
          &times;
        </button>
      </header>

      <div class="panel-body">
        <slot />
      </div>
    </aside>
  </div>
</template>

<style scoped>
.scrim {
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  justify-content: flex-end;
  background: rgba(0, 0, 0, 0.32);
}

.panel {
  display: flex;
  flex-direction: column;
  max-width: 100vw;
  background: var(--rl-bg);
  box-shadow: var(--shadow-lg);
}

.panel:focus-visible {
  outline: none;
}

.panel-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--sp-3);
  padding: var(--sp-5);
  border-bottom: 1px solid var(--rl-border-subtle);
}

.panel-title {
  margin: 0;
  font-size: var(--rl-text-lg);
  font-weight: 600;
  letter-spacing: -0.02em;
}

.panel-sub {
  margin: var(--sp-1) 0 0;
  font-size: var(--rl-text-sm);
  color: var(--rl-fg-3);
}

.panel-close {
  flex: none;
  width: 28px;
  height: 28px;
  font-size: 20px;
  line-height: 1;
  color: var(--rl-fg-3);
  cursor: pointer;
  background: none;
  border: none;
  border-radius: var(--radius-sm);
}

.panel-close:hover {
  color: var(--rl-fg-1);
  background: var(--rl-bg-muted);
}

.panel-body {
  flex: 1;
  min-height: 0;
  padding: var(--sp-5);
  overflow-y: auto;
}
</style>
